<script setup lang="ts">
// Reusable odds widget for the scoreboard. Same selection model and place-bet
// flow as the sportsbook book (via useBetSlip + the shared BetSlip component), so
// a wager started here is identical to one started on the book. Two shapes:
//   * mode="league" — a few upcoming games for one league + a "see all" link to
//     the book with that league preselected.
//   * mode="event"  — the single game's line, shown under the game hero.
// The lines render full-width; tapping a price opens the bet slip in a MODAL
// (rather than a fixed side column), which keeps the three markets from being
// squeezed in the narrower scoreboard layout.
// Odds come from the public /odds/{sport} endpoint, fetched client-side only so
// SSR is never blocked (and a crawler never burns odds quota); the widget shows
// its skeleton during SSR and the first client paint, matching cleanly.
import type { SlipGame } from '~/composables/useBetSlip'

const props = withDefaults(defineProps<{
  sport: string
  mode?: 'league' | 'event'
  eventId?: string
  limit?: number
  leagueLabel?: string
  seeMoreTo?: string
  // when true on mobile, show the slip as a sticky card pinned at the top of the
  // board (like the book's mobile slip) instead of the centered modal. Driven by
  // the parent's media query via :is-mobile so there's one source of truth.
  stickySlip?: boolean
  isMobile?: boolean
}>(), { mode: 'league', limit: 4, stickySlip: false, isMobile: false })

// the book-style sticky slip is used only when the host opts in AND we're on a
// narrow viewport; otherwise the price tap opens the modal (desktop, game page).
const useStickySlip = computed(() => props.stickySlip && props.isMobile)

const api = useApi()
const { fmtDateTime, fmtNum } = useClientFormat()
// lets the scoreboard hide the Odds tab when a league has no bettable lines.
const emit = defineEmits<{ (e: 'availability', has: boolean): void }>()
// one slip instance shared by this board's price buttons and the modal slip.
const slip = useBetSlip()
const { sel, selH2h, selSpread, selTotal, isSel, clearSel } = slip

const allGames = ref<SlipGame[]>([])
const pending = ref(true)

async function load() {
  if (!props.sport) { allGames.value = []; pending.value = false; emit('availability', false); return }
  pending.value = true
  try {
    const r = await api.get<{ games: SlipGame[] }>(`/odds/${encodeURIComponent(props.sport)}`)
    allGames.value = r.games || []
  } catch {
    allGames.value = []
  } finally {
    pending.value = false
    // availability reflects what this board would actually show (mode-aware).
    emit('availability', shownGames.value.length > 0)
  }
}
// client-only: keeps SSR fast and the first client render matches the SSR
// skeleton (pending=true on both), so no hydration mismatch.
onMounted(load)
watch(() => props.sport, () => { clearSel(); load() })

const shownGames = computed<SlipGame[]>(() => {
  if (props.mode === 'event') {
    return props.eventId ? allGames.value.filter(g => g.event_id === props.eventId) : []
  }
  return allGames.value.slice(0, props.limit)
})
const emptyText = computed(() => props.mode === 'event'
  ? 'No open line for this game — betting closes once it starts.'
  : `No upcoming lines for ${props.leagueLabel || 'this league'} right now.`)
</script>

<template>
  <section class="odds-board">
    <!-- header: just the "see all" link (the surrounding tab is already labelled
         "Odds", so no duplicate title here). Omitted entirely in event mode. -->
    <div v-if="seeMoreTo && mode === 'league'" class="ob-head">
      <NuxtLink class="ob-more" :to="seeMoreTo">see all {{ leagueLabel || '' }} odds →</NuxtLink>
    </div>

    <!-- mobile (opt-in): the slip pinned at the top, like the book, so there's no
         scrolling back up after a pick. Replaces the modal on narrow screens. -->
    <div v-if="useStickySlip && sel" class="ob-sticky-slip">
      <BetSlip :slip="slip" />
    </div>

    <!-- the league's (or single game's) lines — full width -->
    <div class="ob-games">
      <template v-if="pending">
        <div v-for="n in (mode === 'event' ? 1 : 2)" :key="n" class="event skel">
          <div class="sk-line w60"></div>
          <div class="market-row">
            <div class="sk-box"></div><div class="sk-box"></div><div class="sk-box"></div>
          </div>
        </div>
      </template>

      <template v-else>
        <div v-if="!shownGames.length" class="ob-empty">{{ emptyText }}</div>

        <div v-for="g in shownGames" :key="g.event_id" class="event">
          <div class="matchup">
            <div class="teams">{{ g.away_team }}<span class="vs">@</span>{{ g.home_team }}</div>
            <div class="kick">{{ fmtDateTime(g.commence_time) }}</div>
          </div>
          <div class="markets">
            <div class="market">
              <div class="market-h">moneyline</div>
              <div class="market-row">
                <button v-for="o in g.markets.h2h" :key="'h'+o.name" class="price"
                  :class="{ sel: isSel(g.event_id, 'h2h', o.name) }" @click="selH2h(g, o)">
                  <span class="name">{{ o.name }}</span><span class="odd">{{ fmtNum(o.price) }}</span>
                </button>
                <div v-if="!g.markets.h2h.length" class="price empty">—</div>
              </div>
            </div>
            <div class="market">
              <div class="market-h">spread</div>
              <div class="market-row">
                <button v-for="o in g.markets.spreads" :key="'s'+o.name" class="price"
                  :class="{ sel: isSel(g.event_id, 'spreads', o.name) }" @click="selSpread(g, o)">
                  <span class="name">{{ o.name }} <i v-if="o.point != null">{{ o.point > 0 ? '+' : '' }}{{ o.point }}</i></span>
                  <span class="odd">{{ fmtNum(o.price) }}</span>
                </button>
                <div v-if="!g.markets.spreads.length" class="price empty">—</div>
              </div>
            </div>
            <div class="market">
              <div class="market-h">total</div>
              <div class="market-row">
                <button v-for="o in g.markets.totals" :key="'t'+o.name" class="price"
                  :class="{ sel: isSel(g.event_id, 'totals', o.name) }" @click="selTotal(g, o)">
                  <span class="name">{{ o.name }} <i v-if="o.point != null">{{ o.point }}</i></span>
                  <span class="odd">{{ fmtNum(o.price) }}</span>
                </button>
                <div v-if="!g.markets.totals.length" class="price empty">—</div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- bet slip modal: opens on a price tap; closes on backdrop, the ×, "clear",
         or a successful place (placeBet clears the selection). Suppressed when the
         sticky slip is active (mobile, opt-in) so only one slip shows. -->
    <div v-if="sel && !useStickySlip" class="ob-modal" @click.self="clearSel">
      <div class="ob-modal-card">
        <button class="ob-modal-x" type="button" aria-label="close" @click="clearSel">×</button>
        <BetSlip :slip="slip" />
      </div>
    </div>
  </section>
</template>

<style scoped>
.odds-board { margin-bottom: 22px; }
.ob-head { display: flex; align-items: baseline; justify-content: flex-end; margin-bottom: 12px; }
.ob-more { font-size: 12px; color: var(--accent); white-space: nowrap; font-weight: 700; }
.ob-more:hover { filter: brightness(1.1); }

.ob-games { min-width: 0; }
.ob-empty { color: var(--muted); text-align: center; padding: 30px 16px;
  border: 1px dashed var(--line); border-radius: 12px; }

/* mobile sticky slip (opt-in): pin below the header like the book's slip. The
   +--bt-top-safe clears the header, which grows by the OS safe-area top in the
   native embed. The inner .slip is sticky for the book's side column, so reset it
   to static here. */
.ob-sticky-slip { position: sticky; top: calc(64px + var(--bt-top-safe, 0px)); z-index: 15; margin-bottom: 12px; }
.ob-sticky-slip :deep(.slip) { position: static; top: auto; margin: 0; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }

/* market grid — copied from the lobby so the lines render identically. Now full
   width (no reserved slip column), so the three markets aren't squeezed. */
.markets { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; align-items: stretch; }
.market { display: flex; flex-direction: column; }
.market-h { font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); margin-bottom: 6px; }
.market-row { display: flex; gap: 8px; align-items: stretch; flex: 1; }
.price.empty { color: var(--muted); display: grid; place-items: center; }
.price i { font-style: normal; color: var(--muted); }

/* slip modal */
.ob-modal { position: fixed; inset: 0; z-index: 60; display: grid; place-items: center;
  padding: 20px 20px calc(20px + var(--bt-bottom-safe, 0px)); background: rgba(8, 5, 20, 0.66); backdrop-filter: blur(2px);
  animation: ob-fade .12s ease-out; }
.ob-modal-card { position: relative; width: 100%; max-width: 360px; }
/* the global .slip is sticky for the book's side column; inside the modal it
   should sit naturally. */
.ob-modal-card :deep(.slip) { position: static; top: auto; margin: 0; }
.ob-modal-x { position: absolute; top: 10px; right: 12px; z-index: 1; width: 28px; height: 28px;
  border-radius: 8px; background: none; border: 1px solid var(--line); color: var(--muted);
  font-size: 18px; line-height: 1; cursor: pointer; }
.ob-modal-x:hover { color: var(--ink); border-color: var(--brand); }
@keyframes ob-fade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .ob-modal { animation: none; } }

@media (max-width: 900px) {
  .markets { grid-template-columns: 1fr; gap: 10px; }
}
</style>
